import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import * as authService from '../../services/auth';
import { buildApiUrl } from '../../config/api';

interface VerificationState {
  loading: boolean;
  success: boolean;
  error: string | null;
  message: string;
}

export const EmailVerificationPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [state, setState] = useState<VerificationState>({
    loading: true,
    success: false,
    error: null,
    message: ''
  });

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setState({
        loading: false,
        success: false,
        error: '缺少验证令牌',
        message: '验证链接无效，请检查邮箱中的链接是否完整。'
      });
      return;
    }

    verifyEmail(token);
  }, [token]);

  const verifyEmail = async (verificationToken: string) => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));
      
      const response = await fetch(buildApiUrl('/api/v1/auth/verify-email'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: verificationToken }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setState({
          loading: false,
          success: true,
          error: null,
          message: '邮箱验证成功！您现在可以使用所有功能。'
        });
        
        // 3秒后跳转到登录页面
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 3000);
      } else {
        setState({
          loading: false,
          success: false,
          error: data.error?.message || '验证失败',
          message: '验证令牌无效或已过期，请重新请求验证邮件。'
        });
      }
    } catch (error) {
      setState({
        loading: false,
        success: false,
        error: '网络错误',
        message: '无法连接到服务器，请检查网络连接后重试。'
      });
    }
  };

  const handleBackToLogin = () => {
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">PromptMaster</h1>
          <p className="text-gray-600">邮箱验证</p>
        </div>
        
        <div className="bg-white p-8 rounded-lg shadow-md">
          {state.loading && (
            <div className="text-center">
              <div className="inline-flex items-center">
                <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-lg text-gray-600">正在验证邮箱...</span>
              </div>
            </div>
          )}

          {!state.loading && state.success && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">验证成功！</h2>
              <p className="text-gray-600 mb-6">{state.message}</p>
              <p className="text-sm text-gray-500">3秒后自动跳转到登录页面...</p>
            </div>
          )}

          {!state.loading && !state.success && (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">验证失败</h2>
              <p className="text-gray-600 mb-2">{state.message}</p>
              {state.error && (
                <p className="text-sm text-red-600 mb-6">错误: {state.error}</p>
              )}
              <button
                onClick={handleBackToLogin}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                返回登录页面
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 